import React from 'react';

// Helpers para renderizar o topAlarm vindo de /parks/for-map. Centralizamos
// aqui tudo que envolve formatacao da causa (texto + tempo + icones de
// direcao) pra alert-overlay.js e marker-detail-panel.js compartilharem
// a mesma logica.
//
// Shape do topAlarm (definido no backend, ver TopAlarmDTO):
//   {
//     variable: string,        // ihmName ("Pressão Saída")
//     applianceName: string,   // ("UCHA-01")
//     value: number,
//     unit: string,
//     threshold: number,
//     direction: 'high' | 'low',
//     level: 'EH' | 'EL' | 'WH' | 'WL',
//     since: number             // epoch (segundos)
//   }

// Triangulo de alerta Lucide-style. Usado quando o topAlarm vem do
// firmware (source='firmware') — nao temos valor/limite numerico, so
// um sinalizador generico de 'alarme de dispositivo'.
export const AlertTriangleIcon = ({ size = 12 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

// Setas Lucide-style. 'high' = trending up (▲), 'low' = trending down (▼).
// stroke=currentColor — herda do severityColor aplicado no wrapper.
export const ArrowUpIcon = ({ size = 12 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="m18 15-6-6-6 6" />
  </svg>
);

export const ArrowDownIcon = ({ size = 12 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="m6 9 6 6 6-6" />
  </svg>
);

// Severidade -> cor semantica. EH/EL = erro (vermelho), WH/WL = atencao
// (amarelo/laranja). Retorna a CHAVE da var CSS, e o consumidor decide
// usar var(--cco-red) ou theme.colors.red.
export const getSeverityKey = (level = '') => {
  if (level === 'EH' || level === 'EL') return 'error';
  if (level === 'WH' || level === 'WL') return 'warning';
  return 'neutral';
};

// Tempo relativo curto: "agora", "há 3 min", "há 1 h", "há 2 d". Mantemos
// curto pra caber no card lateral sem quebrar linha. Operador entende
// "agora" como qualquer coisa < 60s, suficiente pra triagem.
export const formatTimeAgo = (epochSec) => {
  if (!epochSec || !Number.isFinite(epochSec)) return null;
  const diffSec = Math.max(0, Math.floor(Date.now() / 1000 - epochSec));
  if (diffSec < 60) return 'agora';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `há ${diffMin} min`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `há ${diffHr} h`;
  const diffDay = Math.floor(diffHr / 24);
  return `há ${diffDay} d`;
};

// Duracao crua (sem "há") — usada quando o consumidor quer compor a string.
// Ex: "12 min", "2 h", "1 d". Retorna "<1 min" pra durações <60s.
export const formatDuration = (epochSec) => {
  if (!epochSec || !Number.isFinite(epochSec)) return null;
  const diffSec = Math.max(0, Math.floor(Date.now() / 1000 - epochSec));
  if (diffSec < 60) return '<1 min';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} min`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr} h`;
  const diffDay = Math.floor(diffHr / 24);
  return `${diffDay} d`;
};

// Threshold de SLA: alarmes com mais que isso ganham destaque visual no
// AlertOverlay (cor mais intensa, prioridade no sort). 15min escolhidos
// como default — operador tipicamente reage em <5min, persistir por
// 3x mais que isso indica problema real que merece atencao prioritaria.
export const SLA_OLD_ALARM_MS = 15 * 60 * 1000;
export const isOldAlarm = (epochSec) => {
  if (!epochSec || !Number.isFinite(epochSec)) return false;
  const ageMs = Date.now() - epochSec * 1000;
  return ageMs >= SLA_OLD_ALARM_MS;
};

// Formata um numero pra exibicao compacta. Mantem 1 casa decimal se
// fracionario, inteiro se nao. Evita "45.00000001" feio de IoT.
export const formatNumber = (n) => {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  const num = Number(n);
  if (Number.isInteger(num)) return String(num);
  // Range pequeno (sensor de pressao, temp): 1 casa. Range grande
  // (potencia em kW): mantemos 0 casas via Math.round. Heuristica:
  // se |n| >= 100, sem casa decimal.
  if (Math.abs(num) >= 100) return String(Math.round(num));
  return num.toFixed(1);
};

// Resumo textual da causa pra usar no aria-label/title do AlertItem.
// Varia conforme source. Ex:
//   range:    "Pressão Saída 45.2 bar acima do limite 40 (UCHA-01, há 3 min)"
//   firmware: "Temperatura Estator Fase R (UCHA-01, há 3 min)"
export const buildAlarmAriaLabel = (topAlarm) => {
  if (!topAlarm) return null;
  const { variable, applianceName, since, source } = topAlarm;
  const tAgo = formatTimeAgo(since);
  const tail = [applianceName, tAgo].filter(Boolean).join(', ');

  if (source === 'firmware') {
    return `${variable}${tail ? ` (${tail})` : ''}`;
  }

  const { value, unit, threshold, direction } = topAlarm;
  const dirLabel =
    direction === 'high' ? 'acima do limite' : 'abaixo do limite';
  const parts = [
    `${variable} ${formatNumber(value)}${unit ? ` ${unit}` : ''}`,
    dirLabel,
    formatNumber(threshold),
  ];
  return `${parts.join(' ')}${tail ? ` (${tail})` : ''}`;
};
